import type { LearnosityRequest } from '../models/LearnosityRequest';
import { throwIfAborted } from './fetchCancellation';
import { addLearnosityBreadcrumb } from './learnositySentry';
import {
  LEARNOSITY_RENDERING_DEPENDENCY_HOST,
  LearnosityItemsSdkError,
  LearnositySdkTimeoutError,
  isLearnosityScriptFailure,
  learnosityScriptFailureHost,
} from './learnosityErrors';
import {
  LearnosityTransportError,
  XDOMAIN_FRAME_CLASS,
  hasXdomainFrameAnnounced,
  hasXdomainFrameCarriedRequest,
  learnosityXdomainObserver,
  retireXdomainFrame,
  selectedXdomainFrame,
  waitForXdomainAnnouncement,
} from './learnosityXdomainTransport';

type ItemsSdk = Window['LearnosityItems'];
type ItemsCallbacks = Parameters<ItemsSdk['init']>[1];
type ConsumerState = 'waiting' | 'initializing' | 'ready' | 'failed';
export type LearnosityRecoveryAction = 'xdomain_transport' | 'sdk_reload';
interface Consumer {
  state: ConsumerState;
  released: boolean;
  sdk: ItemsSdk | undefined;
  renderingType?: string;
  errorListener?: ItemsCallbacks['errorListener'];
  settled: Promise<void>;
  settle: () => void;
  cleanup?: () => void;
  startedAt: number;
  deadlineAt: number;
  loaderEpoch: number;
  transportEpoch: number;
  /** The xdomain frame Items core selected for this initialization. */
  generation: HTMLIFrameElement | null;
  missedHandshake?: HTMLIFrameElement;
  recoveryAction?: LearnosityRecoveryAction;
}

/** Items core v1.132.5 waits 10s for its xdomain announcement; allow for scheduling slack. */
const HANDSHAKE_TIMEOUT_MS = 9_500;
/** Wait for announcement before retirement; a still-silent frame fails the rebuild with reload_unsafe. */
const XDOMAIN_ANNOUNCEMENT_WAIT_MS = 10_000;
const GENERIC_INIT_FAILURE = 'Items API initialization failed';

/**
 * SDK replacement shared by every player waiting for it. Preparation can be abandoned;
 * once script loading starts, it must settle before another player can initialize.
 */
class SharedRecovery {
  private readonly waiting = new Set<Consumer>();
  private readonly controller = new AbortController();
  private loading = false;
  readonly done: Promise<void>;

  /** Whether this recovery got as far as replacing the loader. */
  get loadingStarted() {
    return this.loading;
  }

  constructor(
    work: (signal: AbortSignal, beginLoading: () => void) => Promise<void>,
    private readonly deadlineAt: number,
  ) {
    // Defer preparation until the creating player can join and register cancellation.
    this.done = Promise.resolve().then(() =>
      work(this.controller.signal, () => {
        throwIfAborted(this.controller.signal);
        this.loading = true;
      }),
    );
    void this.done.catch(() => undefined);
  }

  async join(consumer: Consumer, signal: AbortSignal): Promise<boolean> {
    throwIfAborted(signal);
    if (this.controller.signal.aborted) {
      // An abandoned preparation cannot acquire new owners. Let its registry entry clear,
      // then the caller can recheck safety and start or join valid work.
      await cancellable(
        this.done.catch(() => undefined),
        signal,
        { at: this.deadlineAt, phase: 'sdk_reload' },
      );
      return false;
    }
    this.waiting.add(consumer);
    const leave = () => {
      this.waiting.delete(consumer);
      if (!this.waiting.size && !this.loading) this.controller.abort();
    };
    // Release interest synchronously, before the preparation's next microtask can start a download.
    signal.addEventListener('abort', leave, { once: true });
    try {
      await cancellable(this.done, signal, { at: this.deadlineAt, phase: 'sdk_reload' });
      return true;
    } finally {
      signal.removeEventListener('abort', leave);
      leave();
    }
  }
}

const consumers = new Set<Consumer>();
// Failures are recorded against the narrowest scope they invalidate: a rendering
// dependency host, or SDK_WIDE for the core every rendering type shares.
const SDK_WIDE = '*';
const failedLoaders = new WeakMap<ItemsSdk, Map<string, unknown>>();
let reload: SharedRecovery | undefined;
// Retirements change which frame Items core selects; reloads replace the core itself.
let transportEpoch = 0;
let loaderEpoch = 0;
// Only a completed core is eligible for reuse; dependency failures are checked per consumer.
let completedLoaderEpoch: number | undefined;

/** Record a script failure against the narrowest scope it invalidates. */
function recordLoaderFailure(sdk: ItemsSdk, error: unknown) {
  // A failed core download invalidates every rendering type; a rendering
  // dependency invalidates only the types that load it.
  const scope = isBootstrapFailure(error) ? SDK_WIDE : learnosityScriptFailureHost(error) ?? SDK_WIDE;
  const failures = failedLoaders.get(sdk) ?? new Map<string, unknown>();
  failures.set(scope, error);
  failedLoaders.set(sdk, failures);
}

/** The recorded failure that blocks this rendering type on this loader, if any. */
function blockingFailure(sdk: ItemsSdk, renderingType: unknown): unknown {
  const failures = failedLoaders.get(sdk);
  if (!failures) return undefined;
  const shared = failures.get(SDK_WIDE);
  if (shared !== undefined) return shared;
  const host = typeof renderingType === 'string' ? LEARNOSITY_RENDERING_DEPENDENCY_HOST[renderingType] : undefined;
  // An unrecognised rendering type may load any dependency, so keep the strict barrier.
  if (!host) return failures.values().next().value;
  return failures.get(host);
}

function settleConsumer(consumer: Consumer, state: 'ready' | 'failed') {
  if (consumer.state !== 'initializing') return;
  consumer.state = state;
  // A premature reset can suppress the callback that settles Items startup. Finish
  // abandoned instances only now, before waiting players can use the same SDK.
  const cleanup = consumer.cleanup;
  consumer.cleanup = undefined;
  cleanup?.();
  consumer.settle();
  if (consumer.released) consumers.delete(consumer);
}

function isBootstrapFailure(error: unknown): error is Parameters<ItemsCallbacks['errorListener']>[0] {
  if (!error || typeof error !== 'object') return false;
  const { code, msg, detail } = error as { code?: unknown; msg?: unknown; detail?: unknown };
  if (
    String(code) !== '50014' ||
    msg !== "Failed loading Items API, can't load required modules" ||
    typeof detail !== 'string' ||
    !detail.startsWith("Couldn't load ")
  )
    return false;
  try {
    const url = new URL(detail.slice("Couldn't load ".length));
    return (
      url.protocol === 'https:' && url.hostname === 'items.learnosity.com' && url.pathname.endsWith('/dist/api.js')
    );
  } catch {
    return false;
  }
}

function cancellable(
  work: Promise<void>,
  signal: AbortSignal,
  deadline?: { at: number; phase: LearnositySdkTimeoutError['phase'] },
): Promise<void> {
  throwIfAborted(signal);
  if (deadline && deadline.at <= performance.now())
    return Promise.reject(new LearnositySdkTimeoutError(deadline.phase));
  return new Promise((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const cleanup = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', abort);
    };
    const fail = (error: unknown) => {
      cleanup();
      reject(error);
    };
    const abort = () => fail(new DOMException('Request cancelled', 'AbortError'));
    signal.addEventListener('abort', abort, { once: true });
    // Expiring a wait never settles the vendor work or releases its safety barrier.
    if (deadline) {
      const expire = () => fail(new LearnositySdkTimeoutError(deadline.phase));
      timer = setTimeout(expire, Math.max(0, deadline.at - performance.now()));
    }
    void work.then(() => {
      cleanup();
      resolve();
    }, fail);
  });
}

function configuredBootstrap() {
  return Array.from(document.scripts).find(script => {
    const url = new URL(script.src, document.baseURI);
    return url.protocol === 'https:' && url.hostname === 'items.learnosity.com' && url.pathname === '/' && !!url.search;
  });
}

function isGenericInitializationFailure(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { code, msg, detail } = error as { code?: unknown; msg?: unknown; detail?: unknown };
  return String(code) === '50004' && msg === GENERIC_INIT_FAILURE && detail === undefined;
}

/** The transport frame whose one-time announcement this failure missed, when the evidence shows it. */
function missedHandshakeGeneration(consumer: Consumer, error: unknown): HTMLIFrameElement | undefined {
  if (!isGenericInitializationFailure(error)) return;
  if (performance.now() - consumer.startedAt < HANDSHAKE_TIMEOUT_MS) return;
  // A retirement or reload since this initialization started means its frame is no longer current.
  if (consumer.transportEpoch !== transportEpoch || consumer.loaderEpoch !== loaderEpoch) return;
  // A queued initialization reached Items core only when the core loaded. With both epochs
  // unchanged, the frame selected now is the one the core used for it.
  const generation = consumer.generation ?? selectedXdomainFrame();
  if (!generation || hasXdomainFrameCarriedRequest(generation)) return;
  return generation;
}

async function announced(frame: HTMLIFrameElement, signal: AbortSignal): Promise<boolean> {
  return hasXdomainFrameAnnounced(frame) || waitForXdomainAnnouncement(frame, XDOMAIN_ANNOUNCEMENT_WAIT_MS, signal);
}

function retire(frame: HTMLIFrameElement, startedAt: number) {
  if (!frame.classList.contains(XDOMAIN_FRAME_CLASS)) return;
  retireXdomainFrame(frame);
  transportEpoch += 1;
  addLearnosityBreadcrumb('xdomain_transport_retired', {
    api: 'items',
    waitedMs: Math.max(0, Math.round(performance.now() - startedAt)),
  });
}

/** Transport recovery can continue after unrelated preparation fails, but not a failed replacement. */
async function waitForSharedRecovery(work: SharedRecovery, consumer: Consumer, signal: AbortSignal) {
  try {
    await work.join(consumer, signal);
  } catch (error) {
    if (work.loadingStarted || error instanceof LearnositySdkTimeoutError) throw error;
    // This player's own cancellation still ends its recovery.
    throwIfAborted(signal);
  }
}

function rebuildSdk(bootstrap: HTMLScriptElement, deadlineAt: number): SharedRecovery {
  return new SharedRecovery(async (signal, beginLoading) => {
    // A rebuilt core reuses any classed xdomain frame, and a frame announces only once. Retire
    // announced frames only, so a late announcement cannot bind the rebuilt core to a frame
    // that is still loading. With no frame there is nothing to retire.
    for (let frame = selectedXdomainFrame(); frame; frame = selectedXdomainFrame()) {
      const startedAt = performance.now();
      if (!(await announced(frame, signal))) throw new LearnosityTransportError('reload_unsafe');
      throwIfAborted(signal);
      retire(frame, startedAt);
    }
    beginLoading();
    loaderEpoch += 1;
    await reloadSdk(bootstrap);
    if (typeof window.LearnosityItems?.init !== 'function') throw new LearnosityItemsSdkError('reload_failed');
    completedLoaderEpoch = loaderEpoch;
  }, deadlineAt);
}

function reloadSdk(bootstrap: HTMLScriptElement): Promise<void> {
  // A fresh bootstrap/core pair creates a fresh vendor dependency cache. Never
  // call the global reset: it would discard every player's unsaved responses.
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = bootstrap.src;
    script.async = true;
    let module: HTMLScriptElement | undefined;
    const finish = (error?: Error) => {
      observer.disconnect();
      script.removeEventListener('error', failed);
      script.removeEventListener('load', bootstrapLoaded);
      if (module) {
        module.removeEventListener('load', loaded);
        module.removeEventListener('error', failed);
      }
      if (error) reject(error);
      else resolve();
    };
    const failed = () => finish(new LearnosityItemsSdkError('reload_failed'));
    const loaded = () => finish();
    const bootstrapLoaded = () => {
      // Allow mutation delivery even when a resource event is dispatched in the
      // same task. A completed script without Items core is an unusable loader.
      queueMicrotask(() => {
        if (!module) failed();
      });
    };
    const observer = new MutationObserver(records => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (!(node instanceof HTMLScriptElement)) continue;
          const url = new URL(node.src, document.baseURI);
          if (url.hostname !== 'items.learnosity.com' || !url.pathname.endsWith('/dist/api.js')) continue;
          module = node;
          node.addEventListener('load', loaded);
          node.addEventListener('error', failed);
        }
      }
    });
    observer.observe(document, { childList: true, subtree: true });
    script.addEventListener('error', failed);
    script.addEventListener('load', bootstrapLoaded);
    // The bootstrap creates its core script synchronously. Its load event does
    // not mean Items API is available; wait for the generated core script.
    const target = window as unknown as { LearnosityItems?: ItemsSdk };
    // Classic scripts create a nonconfigurable global var. Clear its value;
    // deleting the binding throws in modules and silently fails in scripts.
    target.LearnosityItems = undefined;
    document.head.appendChild(script);
  });
}

export function acquireLearnosityItemsSdk(deadlineAt: number) {
  learnosityXdomainObserver();
  let settle!: () => void;
  const consumer: Consumer = {
    state: 'waiting',
    released: false,
    sdk: window.LearnosityItems,
    settled: new Promise<void>(resolve => (settle = resolve)),
    settle: () => settle(),
    startedAt: 0,
    deadlineAt,
    loaderEpoch,
    transportEpoch,
    generation: null,
  };
  consumers.add(consumer);
  return {
    get sdk() {
      return consumer.sdk;
    },
    get recoveryAction() {
      return consumer.recoveryAction;
    },
    waitUntilAvailable: function wait(signal: AbortSignal): Promise<void> | null {
      throwIfAborted(signal);
      const pending = [...consumers].filter(
        entry => entry !== consumer && entry.released && entry.state === 'initializing',
      );
      if (!reload && !pending.length) return null;
      return cancellable(
        Promise.all([
          reload?.join(consumer, signal),
          ...pending.map(entry =>
            cancellable(entry.settled, signal, { at: entry.deadlineAt, phase: 'initialization' }),
          ),
        ]).then(() => undefined),
        signal,
      ).then(() => wait(signal) ?? undefined);
    },
    init(initialization: LearnosityRequest, callbacks: ItemsCallbacks) {
      // Availability can resolve while React hides the player. Only a real SDK call
      // owns unresolved vendor work, and it must use the generation current at that call.
      consumer.sdk = window.LearnosityItems;
      consumer.renderingType = initialization.request?.rendering_type;
      consumer.errorListener = callbacks.errorListener;
      consumer.state = 'initializing';
      consumer.startedAt = performance.now();
      consumer.loaderEpoch = loaderEpoch;
      consumer.transportEpoch = transportEpoch;
      consumer.generation = null;
      if (!consumer.sdk) throw new LearnosityItemsSdkError('bootstrap_unavailable');
      const failure = blockingFailure(consumer.sdk, consumer.renderingType);
      if (failure) throw failure;
      const player = consumer.sdk.init(initialization, callbacks);
      // A loaded core selects or creates its transport frame synchronously. A queued
      // initialization has no frame yet and is resolved when it fails instead.
      consumer.generation = selectedXdomainFrame();
      return player;
    },
    markReady: () => settleConsumer(consumer, 'ready'),
    markFailed: (error: unknown) => {
      if (consumer.state === 'initializing') {
        if (isLearnosityScriptFailure(error) && configuredBootstrap()) {
          const bootstrapFailed = isBootstrapFailure(error);
          if (consumer.sdk) recordLoaderFailure(consumer.sdk, error);
          if (bootstrapFailed && consumer.loaderEpoch === completedLoaderEpoch) completedLoaderEpoch = undefined;
          if (consumer.sdk && bootstrapFailed) {
            // The bootstrap reports its failed core download only to the first queued init.
            // Its entire queue has failed, unlike vendor work that is still unresolved.
            const affected = [...consumers].filter(
              entry =>
                entry.state === 'initializing' &&
                entry.sdk === consumer.sdk &&
                entry.loaderEpoch === consumer.loaderEpoch,
            );
            // Settle everyone before callbacks re-enter markFailed. Compare captured SDKs,
            // not the current global, which normal vendor startup also replaces.
            affected.forEach(entry => settleConsumer(entry, 'failed'));
            for (const entry of affected) {
              if (entry !== consumer && !entry.released) entry.errorListener?.(error);
            }
            return;
          }
        } else consumer.missedHandshake = missedHandshakeGeneration(consumer, error);
      }
      settleConsumer(consumer, 'failed');
    },
    release(cleanup?: () => void) {
      consumer.released = true;
      if (consumer.state === 'initializing') {
        consumer.cleanup ??= cleanup;
      } else {
        cleanup?.();
        consumers.delete(consumer);
      }
    },
    async recover(error: unknown, signal: AbortSignal) {
      throwIfAborted(signal);
      const generation = consumer.missedHandshake;
      if (generation) {
        consumer.recoveryAction = 'xdomain_transport';
        // A rebuild in progress may already have replaced this transport.
        while (reload) await waitForSharedRecovery(reload, consumer, signal);
        // A silent frame can still announce to the retry's listener. Retire only a spent,
        // announced generation that stayed current while signing or another rebuild finished.
        if (
          consumer.loaderEpoch === loaderEpoch &&
          consumer.transportEpoch === transportEpoch &&
          selectedXdomainFrame() === generation &&
          !hasXdomainFrameCarriedRequest(generation) &&
          hasXdomainFrameAnnounced(generation)
        )
          retire(generation, performance.now());
        return;
      }
      const bootstrap = configuredBootstrap();
      const unavailable = error instanceof LearnosityItemsSdkError && error.kind === 'bootstrap_unavailable';
      if ((!isLearnosityScriptFailure(error) && !unavailable) || !bootstrap) return;
      consumer.recoveryAction = 'sdk_reload';
      for (;;) {
        throwIfAborted(signal);
        if (reload) {
          // This branch needs the replacement itself, and reaches here as the creator of
          // the shared work too, so it keeps adopting the preparation's outcome.
          await reload.join(consumer, signal);
          continue;
        }
        // Another failed player may have rebuilt while this one refreshed signing.
        if (
          consumer.loaderEpoch < loaderEpoch &&
          completedLoaderEpoch === loaderEpoch &&
          typeof window.LearnosityItems?.init === 'function' &&
          !blockingFailure(window.LearnosityItems, consumer.renderingType)
        )
          return;
        if ([...consumers].some(entry => entry !== consumer && entry.state !== 'waiting' && entry.state !== 'failed')) {
          throw new LearnosityItemsSdkError('reload_blocked');
        }
        const work = rebuildSdk(bootstrap, consumer.deadlineAt);
        reload = work;
        // Removing a script does not reliably stop execution. Even if its
        // caller cancels, serialize new starts until this resource settles.
        void work.done
          .finally(() => {
            if (reload === work) reload = undefined;
          })
          .catch(() => undefined);
      }
    },
  };
}
